import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Playlist, Plus, Check } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useAuth, API } from '../App';
import { toast } from 'sonner';

export function AddToPlaylistModal({ open, onOpenChange, trackId }) {
  const { user } = useAuth();
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addingTo, setAddingTo] = useState(null);
  const [addedTo, setAddedTo] = useState(new Set());
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');

  const isPremium = user?.subscription_tier?.startsWith('premium');

  useEffect(() => {
    if (open && isPremium) {
      fetchPlaylists();
    }
  }, [open, isPremium]);

  const fetchPlaylists = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/playlists`);
      setPlaylists(response.data);
      const alreadyIn = new Set();
      response.data.forEach(p => {
        if (p.track_ids?.includes(trackId)) alreadyIn.add(p.playlist_id);
      });
      setAddedTo(alreadyIn);
    } catch {
      toast.error('Failed to load playlists');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToPlaylist = async (playlistId) => {
    setAddingTo(playlistId);
    try {
      await axios.post(`${API}/playlists/${playlistId}/tracks/${trackId}`);
      setAddedTo(prev => new Set([...prev, playlistId]));
      toast.success('Added to playlist');
    } catch {
      toast.error('Failed to add track');
    } finally {
      setAddingTo(null);
    }
  };

  const handleCreateAndAdd = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const res = await axios.post(`${API}/playlists`, { name: newName, description: '', is_public: false });
      await axios.post(`${API}/playlists/${res.data.playlist_id}/tracks/${trackId}`);
      setPlaylists([...playlists, { ...res.data, track_ids: [trackId], track_count: 1 }]);
      setAddedTo(prev => new Set([...prev, res.data.playlist_id]));
      setNewName('');
      toast.success('Created playlist & added track');
    } catch {
      toast.error('Failed to create playlist');
    } finally {
      setCreating(false);
    }
  };

  if (!isPremium) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="bg-[#141417] border-white/10 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white">Premium Feature</DialogTitle>
          </DialogHeader>
          <p className="text-white/60 text-sm">Upgrade to Premium to create playlists and save your favorite tracks.</p>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#141417] border-white/10 max-w-sm" data-testid="add-to-playlist-modal">
        <DialogHeader>
          <DialogTitle className="text-white">Add to Playlist</DialogTitle>
        </DialogHeader>

        {/* Create new playlist inline */}
        <div className="flex gap-2 mt-2">
          <Input
            placeholder="New playlist name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            className="bg-white/5 border-white/10 text-white text-sm flex-1"
            data-testid="new-playlist-inline-input"
            onKeyDown={(e) => e.key === 'Enter' && handleCreateAndAdd()}
          />
          <Button
            size="sm"
            onClick={handleCreateAndAdd}
            disabled={creating || !newName.trim()}
            className="bg-[#F59E0B] text-black hover:bg-[#D97706] px-3 shrink-0"
            data-testid="create-and-add-btn"
          >
            <Plus className="w-4 h-4" />
          </Button>
        </div>

        {/* Playlist list */}
        <div className="mt-3 max-h-64 overflow-y-auto space-y-1">
          {loading ? (
            <div className="flex justify-center py-6">
              <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-[#F59E0B]"></div>
            </div>
          ) : playlists.length === 0 ? (
            <p className="text-white/40 text-sm text-center py-4">No playlists yet. Create one above!</p>
          ) : (
            playlists.map(pl => {
              const isAdded = addedTo.has(pl.playlist_id);
              const isAdding = addingTo === pl.playlist_id;
              return (
                <button
                  key={pl.playlist_id}
                  onClick={() => !isAdded && handleAddToPlaylist(pl.playlist_id)}
                  disabled={isAdded || isAdding}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors text-left ${
                    isAdded ? 'bg-[#F59E0B]/10' : 'hover:bg-white/5'
                  }`}
                  data-testid={`playlist-option-${pl.playlist_id}`}
                >
                  <div className="w-10 h-10 rounded-md bg-gradient-to-br from-[#F59E0B]/20 to-[#D97706]/10 flex items-center justify-center shrink-0">
                    {isAdded ? (
                      <Check className="text-[#F59E0B]" />
                    ) : (
                      <Playlist className="text-[#F59E0B]" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium truncate">{pl.name}</p>
                    <p className="text-white/40 text-xs" data-testid={`add-to-playlist-count-${pl.playlist_id}`}>{pl.track_count ?? pl.track_ids?.length ?? 0} tracks</p>
                  </div>
                  {isAdding && (
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-[#F59E0B]"></div>
                  )}
                </button>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
